/** Background disk-backup scheduler attached to the app (authenticated) lifecycle.
 *
 * This component owns the scheduler from the moment the app becomes active and
 * cleans it up when it leaves. The engine's `status` line is the source of truth
 * for the settings panel; detailed backup status is available in Settings → Backup & Restore.
 *
 * The scheduler is *not* mounted on marketing pages or auth gates, and it is
 * stopped on unmount, so a browser tab that is hidden still keeps its timer but
 * an unmounted app never runs a tick. The engine's monotonic manifest cursor
 * plus the gap-checked journal make a late tick harmless (see backupScheduler.ts).
 *
 * This component does NOT render a visible status overlay.
 */
import { useEffect } from 'react';
import { getDb } from '@data/db/database';
import type { ResolvedTarget } from '@data/backup/resolveDiskBackupTarget';
import {
  AUTO_BACKUP_SCHEDULER_TOKEN,
  disposeBackupScheduler,
  ensureAutoBackupScheduler,
} from '@data/backup/backupScheduler';
import { runDiskBackup } from '@data/backup/localBackupEngine';
import { useAuth } from '@platform/auth/authContext';

export function AutoBackupScheduler() {
  const { user } = useAuth();
  useEffect(() => {
    let active = true;

    void ensureAutoBackupScheduler({
      onRun: (target: ResolvedTarget, now: string) =>
        runDiskBackup(target.target, getDb(), now, user?.id),
    }).catch((error) => {
      if (active) {
        console.warn('Auto backup scheduler error:', error);
      }
    });

    return () => {
      active = false;
      disposeBackupScheduler(AUTO_BACKUP_SCHEDULER_TOKEN);
    };
  }, [user?.id]);

  return null;
}